import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamWorkspaceService } from '../lib/teamWorkspaceService';
import { WorkspaceSummary, ShareSummaryRequest } from '../types';

export const useWorkspaceSummaries = (workspaceId: string | null) => {
  return useQuery({
    queryKey: ['workspace-summaries', workspaceId],
    queryFn: () => teamWorkspaceService.getWorkspaceSummaries(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 30000,
  });
};

export const useWorkspaceComments = (workspaceId: string | null, sharedSummaryId?: string) => {
  return useQuery({
    queryKey: ['workspace-comments', workspaceId, sharedSummaryId],
    queryFn: () => teamWorkspaceService.getWorkspaceComments(workspaceId!, sharedSummaryId),
    enabled: !!workspaceId,
    staleTime: 10000,
  });
};

export const useShareSummary = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (request: ShareSummaryRequest) => teamWorkspaceService.shareSummary(request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-summaries', variables.workspaceId] });
    },
  });
};

export const useRemoveSharedSummary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sharedSummaryId, workspaceId }: { sharedSummaryId: string; workspaceId: string }) =>
      teamWorkspaceService.removeSharedSummary({ sharedSummaryId, workspaceId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-summaries'] });
    },
  });
};

export const useAddComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ workspaceId, content, sharedSummaryId }: { workspaceId: string; content: string; sharedSummaryId?: string }) =>
      teamWorkspaceService.addWorkspaceComment(workspaceId, content, sharedSummaryId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workspace-comments', variables.workspaceId] });
    },
  });
};

export const useUpdateComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      teamWorkspaceService.updateWorkspaceComment(commentId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-comments'] });
    },
  });
};

export const useDeleteComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: teamWorkspaceService.deleteWorkspaceComment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-comments'] });
    },
  });
};

